/**
 * Everything that decides which wizard screens the navigation state may point
 * at. It knows the workflow order and nothing about the store that uses it.
 */

import type { Option, Screen } from '../types';
import { getWorkflowSteps, screenMeta } from '../config/workflowUi';

export interface NavigationSnapshot {
  screen: Screen;
  navigationHistory: Screen[];
}

const knownScreens = new Set(Object.keys(screenMeta) as Screen[]);
const knownOptions = new Set<Exclude<Option, null>>([
  'Iso Drawings Generation',
  'Pressure Drop Calculations',
  'Stress Calculations',
]);

export const isKnownScreen = (value: unknown): value is Screen =>
  typeof value === 'string' && knownScreens.has(value as Screen);

export const isKnownOption = (value: unknown): value is Option =>
  value === null || (typeof value === 'string' && knownOptions.has(value as Exclude<Option, null>));

const getOrderedWorkflowScreens = (selectedOption: Option, screen: Screen): Screen[] =>
  getWorkflowSteps(selectedOption, screen).map((step) => step.screen);

/**
 * Rebuilds the reachable path up to the furthest screen the user ever reached,
 * dropping anything the current workflow no longer contains.
 */
export const normalizeNavigationState = (
  screen: Screen,
  navigationHistory: Screen[],
  selectedOption: Option
): NavigationSnapshot => {
  const orderedWorkflowScreens = getOrderedWorkflowScreens(selectedOption, screen);
  const workflowStepLookup = new Map(
    orderedWorkflowScreens.map((workflowScreen, index) => [workflowScreen, index])
  );
  const distinctHistory = Array.from(new Set(navigationHistory.filter(isKnownScreen)));
  const validHistory = distinctHistory.filter((historyScreen) =>
    workflowStepLookup.has(historyScreen)
  );
  const fallbackScreen = validHistory[validHistory.length - 1] ?? 'welcome';
  const normalizedScreen = workflowStepLookup.has(screen) ? screen : fallbackScreen;
  const furthestReachedStepIndex = Math.max(
    workflowStepLookup.get(normalizedScreen) ?? 0,
    ...validHistory.map((historyScreen) => workflowStepLookup.get(historyScreen) ?? 0)
  );
  const orderedHistory = orderedWorkflowScreens.slice(0, furthestReachedStepIndex + 1);

  return {
    screen: normalizedScreen,
    navigationHistory: orderedHistory.length > 0 ? orderedHistory : ['welcome'],
  };
};

/**
 * Cuts reachability back to the boundary screen a new setup revision owns.
 * Unlike {@link normalizeNavigationState} it deliberately forgets every later
 * screen: having visited a forward step earlier is not committed reachability
 * once the user returns to setup.
 */
export const truncateWorkflowReachability = (
  boundaryScreen: Screen,
  selectedOption: Option
): NavigationSnapshot => {
  const orderedWorkflowScreens = getOrderedWorkflowScreens(selectedOption, boundaryScreen);
  const boundaryIndex = orderedWorkflowScreens.indexOf(boundaryScreen);

  if (boundaryIndex < 0) {
    return normalizeNavigationState(boundaryScreen, [boundaryScreen], selectedOption);
  }

  return {
    screen: boundaryScreen,
    navigationHistory: orderedWorkflowScreens.slice(0, boundaryIndex + 1),
  };
};

export const getCurrentHistoryIndex = (
  navigationHistory: Screen[],
  currentScreen: Screen
): number => navigationHistory.indexOf(currentScreen);

/** The screen a plain "back" step lands on, or the current one when there is none. */
export const getPreviousReachableScreen = (
  navigationHistory: Screen[],
  currentScreen: Screen
): Screen => {
  const currentHistoryIndex = getCurrentHistoryIndex(navigationHistory, currentScreen);
  return currentHistoryIndex > 0 ? navigationHistory[currentHistoryIndex - 1] : currentScreen;
};
